/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// psu-view.js — a power-supply brick on the desk (.layer-parts): rounded
// body, voltage badge, and the red `+` / black `−` terminal pads whose
// centers are the addressable wire points (psu1.+ / psu1.-). Drawn once;
// the badge text updates when the voltage changes. Under Spice Lite, a
// readout between the badge and the terminals shows the current being drawn
// while the circuit runs — and, past the supply's limit, the voltage it has
// drooped to, in amber (`setSupply`, fed from `chiphippo:sim-state`).

import { svgEl } from "../dom.js";
import { formatNumber } from "../i18n.js";
import { PX_PER_UNIT } from "../desk/desk-geometry.js";
import { partDef } from "../catalog/index.js";
import { BrickView } from "./brick-view.js";

/**
 * Build a PSU brick's SVG from the catalog def + params. Pure DOM
 * construction (unit-testable under jsdom).
 */
export function buildPsuSvg(params = {}) {
  const def = partDef("psu");
  const { width, height } = def.size;
  const { volts } = def.normalizeParams(params);

  const svg = svgEl("svg", {
    class: "part-psu-svg",
    viewBox: `0 0 ${width} ${height}`,
    width: width * PX_PER_UNIT,
    height: height * PX_PER_UNIT,
    "aria-hidden": "true",
  });

  svg.append(
    svgEl("rect", {
      class: "part-psu-body",
      x: 0.1,
      y: 0.1,
      width: width - 0.2,
      height: height - 0.2,
      rx: 0.5,
    }),
  );

  const badge = svgEl("text", {
    class: "part-psu-badge",
    x: width / 2,
    y: 1.9,
    "text-anchor": "middle",
  });
  badge.textContent = `${volts} V`;
  svg.append(badge);

  // Spice Lite's live readout — empty (and so invisible) until a run says.
  svg.append(
    svgEl("text", {
      class: "part-psu-readout",
      x: width / 2,
      y: 2.95,
      "text-anchor": "middle",
    }),
  );

  for (const t of def.terminals) {
    const plus = t.id === "+";
    svg.append(
      svgEl("circle", {
        class: `part-psu-terminal part-psu-terminal--${plus ? "plus" : "minus"}`,
        cx: t.dx,
        cy: t.dy,
        r: 0.55,
      }),
    );
    const glyph = svgEl("text", {
      class: "part-psu-terminal-glyph",
      x: t.dx,
      y: t.dy + 0.22,
      "text-anchor": "middle",
    });
    glyph.textContent = plus ? "+" : "−";
    svg.append(glyph);
  }
  return svg;
}

export class PsuView extends BrickView {
  /**
   * @param {HTMLElement} layer - the `.layer-parts` element.
   * @param {{id:string,x:number,y:number,params:object}} psu
   * @param {object} [callbacks]
   * @param {(id: string, e: PointerEvent) => void} [callbacks.onPointerDown]
   * @param {(id: string, e: MouseEvent) => void} [callbacks.onContextMenu]
   */
  constructor(layer, psu, callbacks = {}) {
    super(layer, psu, "part-psu", callbacks);
    this.updateParams(psu.params);
  }

  /** What the readout last showed, so a rebuild keeps it and a tick that
      changes nothing writes nothing. */
  #supply = null;
  #shown = "";

  /** Rebuild the SVG (the badge shows the current volts). */
  updateParams(params) {
    this.element.querySelector("svg")?.remove();
    this.element.prepend(buildPsuSvg(params));
    this.#shown = null;
    this.setSupply(this.#supply);
  }

  /**
   * Show what the supply is delivering (Spice Lite), or nothing.
   * @param {{volts: number, amps: number, limited: boolean}|null} supply
   */
  setSupply(supply) {
    this.#supply = supply ?? null;
    const text = supply ? supplyReadout(supply) : "";
    if (text === this.#shown) return;
    this.#shown = text;
    const readout = this.element.querySelector(".part-psu-readout");
    if (readout) {
      readout.textContent = text;
      fitReadout(readout, text);
    }
    this.element.classList.toggle("part-psu--limited", Boolean(supply?.limited)); // prettier-ignore
  }
}

// The readout is set at the clock's rate size (0.99 units, app.css), where a
// monospace glyph is ~0.6 em wide: 12 characters fill the 7.4 units inside the
// body's rounded corners. "350 mA" never comes close; the at-limit
// "4.62 V · 350 mA" is 15, so that one line is squeezed to the body rather
// than spilling past its edges.
const READOUT_FIT_CHARS = 12;
const READOUT_MAX_WIDTH = 7.4;

function fitReadout(readout, text) {
  if (text.length > READOUT_FIT_CHARS) {
    readout.setAttribute("textLength", READOUT_MAX_WIDTH);
    readout.setAttribute("lengthAdjust", "spacingAndGlyphs");
  } else {
    readout.removeAttribute("textLength");
    readout.removeAttribute("lengthAdjust");
  }
}

/** A current as the readout says it: "35 mA", "1.20 A". */
function ampsText(amps) {
  return amps < 1
    ? `${formatNumber(amps * 1000, { maximumFractionDigits: 0 })} mA`
    : `${formatNumber(amps, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} A`; // prettier-ignore
}

/**
 * The readout: the current drawn — and, at the limit, the drooped voltage
 * before it. Numbers and unit symbols only, so nothing here is a word.
 * @param {{volts: number, amps: number, limited: boolean}} supply
 */
export function supplyReadout({ volts, amps, limited }) {
  const current = ampsText(amps);
  if (!limited) return current;
  const v = formatNumber(volts, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); // prettier-ignore
  return `${v} V · ${current}`;
}
